import React, { useEffect, useState } from 'react'

import type { Accept } from 'react-dropzone'
import Dropzone from 'react-dropzone'
import type { Area } from 'react-easy-crop'
import Cropper from 'react-easy-crop'
import { twMerge } from 'tailwind-merge'

import { classNames } from '@/utils/helpers'

import getCroppedImg from './utils'

interface Props {
  accept?: Accept
  selected?: string
  bgClassNames?: string
  textClassNames?: string
  title?: string
  onSelect: (selected: string) => void
  mode: 'light' | 'dark'
}

export const AgencyLogoUploadField = React.forwardRef<Blob | null, Props>(
  ({ accept, selected, title, onSelect, mode }, ref) => {
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>()
    const [crop, setCrop] = useState({
      x: 0,
      y: 0,
    })
    const [scale, setScale] = useState<number>(1)
    const [cropSize, setCropSize] = useState({
      width: 0,
      height: 0,
    })
    const [mediaSize, setMediaSize] = useState({
      width: 0,
      height: 0,
    })

    useEffect(() => {
      if (cropSize.width && cropSize.height) {
        setScale(
          Math.min(
            cropSize.width / mediaSize.width,
            cropSize.height / mediaSize.height,
          ) * 0.95,
        )
      }
    }, [cropSize, mediaSize])

    useEffect(() => {
      if (selected && croppedAreaPixels) {
        getCroppedImg(selected, croppedAreaPixels).then((value) => {
          if (value && ref && typeof ref !== 'function') {
            ref.current = value
          }
        })
      }
    }, [selected, croppedAreaPixels, onSelect, ref])

    const onDrop = (acceptedFiles: File[]) => {
      const reader = new FileReader()

      if (acceptedFiles.length === 0) return

      reader.onload = () => {
        const dataURL = reader.result

        onSelect(dataURL as string)
      }
      reader.readAsDataURL(acceptedFiles[0])
    }

    return (
      <div className={twMerge('flex flex-col justify-center pt-6')}>
        <h5 className="text-center text-xl font-medium">{title}</h5>
        <Dropzone
          onDrop={onDrop}
          accept={accept}
        >
          {({ getRootProps, getInputProps }) => (
            <div className="mx-auto inline-flex flex-col gap-4 p-4">
              {selected && (
                <div
                  className={twMerge(
                    'overflow-non relative h-[234px] w-[500px]',
                  )}
                >
                  <Cropper
                    image={selected}
                    crop={crop}
                    zoom={scale}
                    aspect={6 / 1}
                    maxZoom={2}
                    restrictPosition={false}
                    onCropChange={setCrop}
                    onCropComplete={(croppedArea, croppedAreaPixels) => {
                      setCroppedAreaPixels(croppedAreaPixels)
                    }}
                    onZoomChange={setScale}
                    minZoom={0.1}
                    setCropSize={({ width, height }) => {
                      setCropSize({ width, height })
                    }}
                    setMediaSize={({ width, height }) => {
                      setMediaSize({ width, height })
                    }}
                    classes={{
                      containerClassName: classNames(
                        mode === 'light' && 'bg-white',
                        mode === 'dark' && 'bg-secondary-600',
                        'p-4',
                      ),
                      cropAreaClassName: classNames(
                        mode === 'light' &&
                          '!border-border-default after:!border-border-default before:!border-border-default',
                      ),
                    }}
                  />
                </div>
              )}
              {!selected && (
                <div
                  className={twMerge(
                    'border-bor group flex h-[234px] w-[500px] cursor-pointer flex-col items-center justify-center rounded-sm border border-secondary-200 bg-secondary-50 text-center transition',
                    mode === 'light' &&
                      'bg-white text-black hover:border-primary-200 hover:bg-primary-50',
                    mode === 'dark' &&
                      'bg-secondary-600 text-white hover:border-primary-200 hover:bg-secondary-700',
                  )}
                  {...getRootProps()}
                >
                  <input
                    {...getInputProps()}
                    className={twMerge(
                      mode === 'light' && 'bg-white ',
                      mode === 'dark' && 'bg-secondary-600 ',
                    )}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                    fill="currentColor"
                    className="h-12 w-12 text-secondary-400 transition group-hover:text-primary-500"
                  >
                    <path
                      fill="none"
                      d="M0 0h24v24H0z"
                    />
                    <path d="M4 19h16v-7h2v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-8h2v7zm9-10v7h-2V9H6l6-6 6 6h-5z" />
                  </svg>
                  {/* eslint-disable-next-line no-inline-styles/no-inline-styles */}
                  <span style={{ color: 'currentColor' }}>
                    Drag & drop your files here or{' '}
                    <span className="mt-5 inline-block text-blue-500 underline">
                      choose file
                    </span>
                  </span>
                </div>
              )}
            </div>
          )}
        </Dropzone>
        <div className="flex w-60 flex-col items-center justify-center gap-4 self-center text-black dark:text-white">
          <div className="flex w-full justify-between">
            <p>Zoom</p>
            <p>{scale.toFixed(1)}</p>
          </div>
          <input
            disabled={!selected}
            className={classNames(
              'transparent h-1.5 w-60 cursor-pointer appearance-none rounded-lg border-transparent bg-secondary-200 accent-primary-500',
              !selected && 'cursor-not-allowed opacity-50',
            )}
            type="range"
            min={0.1}
            max={2}
            step={0.01}
            value={scale}
            onChange={(event) => setScale(parseFloat(event.target.value))}
          />
        </div>
      </div>
    )
  },
)

AgencyLogoUploadField.displayName = 'AgencyLogoUploadField'
